import React, { type ReactNode, type FC } from 'react';
import { isoToFormattedString } from '../utils/date';
import type IsoDate from '../models/IsoDate';
import type Symptoms from '../models/SymptomsModel';
import type CycleCompareSymptoms from '../models/CycleCompareSymptomsModel';

type ChartCompareTableProps = {
    cycleCompareSymptoms: CycleCompareSymptoms;
    algorithm: 'st' | 'marquette';
};

const ChartCompareTable: FC<ChartCompareTableProps> = ({
    cycleCompareSymptoms,
    algorithm,
}) => {
    const numberRows = Math.max(
        35,
        ...cycleCompareSymptoms.map((cycle) => Object.keys(cycle).length),
    );

    return (
        <div className="text-primary flex flex-row relative">
            <DayColumn numRows={numberRows} />
            {cycleCompareSymptoms.map((cycle, index) => (
                <CycleColumn
                    key={Object.keys(cycle)[0]}
                    start={Object.keys(cycle)[0]}
                    symptomData={cycle}
                    algorithm={algorithm}
                    numRows={numberRows}
                    index={index}
                />
            ))}
        </div>
    );
};

type DayColumnProps = {
    numRows: number;
};

const DayColumn = (props: DayColumnProps) => {
    const keys: ReactNode[] = [];
    for (let i = 1; i <= props.numRows; i++) {
        const backgroundColor = i % 2 === 0 ? 'bg-gray' : 'bg-white';
        keys.push(
            <div
                key={i}
                className={`h-8 w-24 flex items-center justify-center ${backgroundColor}`}
            >
                {i}
            </div>,
        );
    }

    return (
        <div>
            <div className="h-8 w-24">Day</div>
            <div className="h-8 w-24" />
            {keys}
        </div>
    );
};

type CycleColumnProps = {
    numRows: number;
    symptomData: Record<IsoDate, Symptoms>;
    start: IsoDate;
    index: number;
    algorithm: 'st' | 'marquette';
};

const CycleColumn: FC<CycleColumnProps> = ({
    numRows,
    symptomData,
    start,
    index,
    algorithm,
}) => {
    const boxes: ReactNode[] = [];
    for (let i = 0; i < numRows; i++) {
        let text = '';
        const backgroundColor = i % 2 ? 'bg-gray' : 'bg-white';
        let dataClasses = '';
        if (i < Object.keys(symptomData).length) {
            const symptoms = Object.values(symptomData)[i];
            // eslint-disable-next-line unicorn/prefer-switch
            if (symptoms.bleeding === 'heavy') {
                dataClasses = 'bg-heavyBleeding';
            } else if (symptoms.bleeding === 'light') {
                dataClasses = 'bg-lightBleeding';
            } else if (symptoms.bleeding === 'spotting') {
                dataClasses = 'bg-spotting';
            } else if (algorithm === 'marquette') {
                switch (symptoms.fertilityMonitor) {
                    case 'low': {
                        text = 'L';
                        break;
                    }

                    case 'high': {
                        text = 'H';
                        break;
                    }

                    case 'peak': {
                        text = '☺';
                        break;
                    }

                    default: {
                        dataClasses = 'bg-white border-2';
                        break;
                    }
                }
            } else if (symptoms.peakDay) {
                dataClasses = 'bg-moreFertile';
                text = 'P';
            } else if (symptoms.mucusSymbol === undefined) {
                dataClasses = 'bg-white border-2';
            } else {
                switch (symptoms.mucusSymbol) {
                    case 'moreFertile': {
                        dataClasses = 'bg-moreFertile';
                        break;
                    }

                    case 'lessFertile': {
                        dataClasses = 'bg-lessFertile';
                        break;
                    }

                    default: {
                        dataClasses = 'bg-notFertile';
                        break;
                    }
                }
            }
        }

        boxes.push(
            <div key={i} className={`h-8 w-24 ${backgroundColor}`}>
                {i < Object.keys(symptomData).length && (
                    <div
                        className={`h-full w-full flex items-center justify-center border-purple ${dataClasses}`}
                    >
                        {text !== '' && <div>{text}</div>}
                    </div>
                )}
            </div>,
        );
    }

    return (
        <div>
            <div className="h-8 w-24">{index === 0 ? 'Cycle' : ' '}</div>
            <div className="h-8 w-24">{isoToFormattedString(start)}</div>
            {boxes}
        </div>
    );
};

export default ChartCompareTable;
